/**
 * Dashboard FE API client — thin wrappers around axios for the
 * /api/v2/dashboard/* endpoints. Mirrors the existing legacy convention
 * (axios + process.env.REACT_APP_SERVER_URL + withCredentials handled
 * by the global axios defaults).
 *
 * All widget endpoints accept an optional `range` query param matching
 * the header dropdown — BE resolves the key into from/to dates and
 * filters the per-entity date column.
 */

import axios from "axios";
import type {
  AgingReport,
  ApiResponse,
  AppointmentsByType,
  AppointmentsSummary,
  BootstrapPayload,
  DashboardBanner,
  EstimatesSummary,
  InvoicesSummary,
  InvoiceStatusFilter,
  LayoutItem,
  ProjectsList,
  RepairRequestsSummary,
  RequestsByType,
  RequestsTrend,
  SchedulesSummary,
  ServiceRequestsList,
  TimeOffUpcoming,
} from "./types";
import type { DashboardRangeValue } from "./RangeContext";

const BASE = `${process.env.REACT_APP_SERVER_URL}/api/v2/dashboard`;

async function unwrap<T>(res: { data: ApiResponse<T> }): Promise<T> {
  const payload = res.data;
  if (payload.success) {
    return payload.data;
  }
  const { error } = payload;
  const message =
    typeof error === "string"
      ? error
      : typeof error === "object" && error && "message" in error
        ? String((error as { message: unknown }).message)
        : "Unknown dashboard API error";
  throw new Error(message);
}

/**
 * Build the shared `{ params: ... }` axios config for the range query.
 * Quick keys ride as `?range=this_week`; custom rides as
 * `?range=custom&from=YYYY-MM-DD&to=YYYY-MM-DD`. Passing no range omits
 * the param entirely (BE falls back to today).
 */
function rangeParams(range?: DashboardRangeValue) {
  if (!range) return undefined;
  if (range.key === "custom") {
    if (!range.fromDate || !range.toDate) {
      // Skip the fetch context for incomplete custom ranges; the BE would
      // 400. Callers should gate on this in the hook layer.
      return undefined;
    }
    return { params: { range: "custom", from: range.fromDate, to: range.toDate } };
  }
  return { params: { range: range.key } };
}

export const fetchBootstrap = async (): Promise<BootstrapPayload> =>
  unwrap(await axios.get(`${BASE}/bootstrap`));

export const fetchLayout = async (): Promise<{ layout: LayoutItem[] }> =>
  unwrap(await axios.get(`${BASE}/layout`));

export const updateLayout = async (
  layout: LayoutItem[],
): Promise<{ layout: LayoutItem[] }> =>
  unwrap(await axios.put(`${BASE}/layout`, { layout }));

export const resetLayout = async (): Promise<{ layout: LayoutItem[] }> =>
  unwrap(await axios.delete(`${BASE}/layout`));

/* --- Widget data endpoints --- */

export const fetchAppointmentsSummary = async (
  range?: DashboardRangeValue,
): Promise<AppointmentsSummary> =>
  unwrap(await axios.get(`${BASE}/appointments-summary`, rangeParams(range)));

export const fetchAppointmentsByType = async (
  range?: DashboardRangeValue,
): Promise<AppointmentsByType> =>
  unwrap(await axios.get(`${BASE}/appointments-by-type`, rangeParams(range)));

export const fetchBanners = async (): Promise<DashboardBanner[]> =>
  unwrap(await axios.get(`${BASE}/banners`));

export const fetchInvoicesSummary = async (
  range?: DashboardRangeValue,
): Promise<InvoicesSummary> =>
  unwrap(await axios.get(`${BASE}/invoices-summary`, rangeParams(range)));

export const fetchEstimatesSummary = async (
  range?: DashboardRangeValue,
): Promise<EstimatesSummary> =>
  unwrap(await axios.get(`${BASE}/estimates-summary`, rangeParams(range)));

export const fetchRepairRequests = async (
  range?: DashboardRangeValue,
): Promise<RepairRequestsSummary> =>
  unwrap(await axios.get(`${BASE}/repair-requests`, rangeParams(range)));

export const fetchRequestsByType = async (
  range?: DashboardRangeValue,
): Promise<RequestsByType> =>
  unwrap(await axios.get(`${BASE}/requests-by-type`, rangeParams(range)));

export const fetchRequestsTrend = async (
  range?: DashboardRangeValue,
): Promise<RequestsTrend> =>
  unwrap(await axios.get(`${BASE}/requests-trend`, rangeParams(range)));

export const fetchServiceRequestsList = async (
  range?: DashboardRangeValue,
): Promise<ServiceRequestsList> =>
  unwrap(await axios.get(`${BASE}/service-requests-list`, rangeParams(range)));

export const fetchProjectsList = async (
  range?: DashboardRangeValue,
): Promise<ProjectsList> =>
  unwrap(await axios.get(`${BASE}/projects-list`, rangeParams(range)));

export const fetchTimeOffUpcoming = async (
  range?: DashboardRangeValue,
): Promise<TimeOffUpcoming> =>
  unwrap(await axios.get(`${BASE}/time-off-upcoming`, rangeParams(range)));

export const fetchSchedulesSummary = async (days = 14): Promise<SchedulesSummary> =>
  unwrap(await axios.get(`${BASE}/schedules-summary`, { params: { days } }));

export const fetchAgingReport = async (
  status: InvoiceStatusFilter = "total",
  range?: DashboardRangeValue,
): Promise<AgingReport> => {
  const rp = rangeParams(range);
  return unwrap(
    await axios.get(`${BASE}/aging-report`, {
      params: { status, ...(rp?.params ?? {}) },
    }),
  );
};
